import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Container, Typography, Paper, TextField,
    Button, Divider, Stack, CircularProgress, Alert, Chip,
    InputAdornment,
} from '@mui/material';
import PersonIcon             from '@mui/icons-material/Person';
import PhoneIcon              from '@mui/icons-material/Phone';
import EmailIcon              from '@mui/icons-material/Email';
import NotesIcon              from '@mui/icons-material/Notes';
import LockIcon               from '@mui/icons-material/Lock';
import EventIcon              from '@mui/icons-material/Event';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useAuth }        from "../context/AuthContext";
import { useCurrency }    from "../context/CurrencyContext";
import { formatCurrency, convertCents, getExchangeRate } from "../utils/currency";
import axios       from "../api/axiosConfig";
import axiosPublic from "../api/axiosPublic";

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';

export default function Checkout() {
    const { id: tourId }    = useParams();
    const { state }         = useLocation();
    const { user, setUser } = useAuth();
    const navigate          = useNavigate();
    const { t }             = useTranslation();

    const { currency, rates } = useCurrency();
    const [loading,      setLoading]      = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bookingError, setBookingError] = useState('');
    const [successBillId, setSuccessBillId] = useState(null);
    const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const tourCurrency = state?.tourCurrency || 'EUR';

    const fmt = (cents) => formatCurrency(
        convertCents(cents, tourCurrency, currency, rates), currency
    );

    const restoredContact = state?.restoredContact;
    const [contact, setContact] = useState({
        fullName: restoredContact?.fullName || user?.username || '',
        email:    user?.email    || '',
        phone:    restoredContact?.phone    || user?.phone    || '',
        notes:    restoredContact?.notes    || '',
    });

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!isSubmitting) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isSubmitting]);

    useEffect(() => {
        if (user) {
            setContact(prev => ({
                ...prev,
                fullName: prev.fullName || user.username || '',
                email:    user.email    || prev.email    || '',
                phone:    prev.phone    || user.phone    || '',
            }));
        }
    }, [user]);

    const handleAuthRedirect = (path) => {
        sessionStorage.setItem('pendingCheckout', JSON.stringify({
            tourId,
            checkoutState: state,
            contact,
        }));
        navigate(path);
    };

    if (!state || !state.tourDate) {
        return (
            <Container sx={{ py: 10 }}>
                <Alert severity="error">{t('checkout.expired')}</Alert>
                <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>{t('checkout.return')}</Button>
            </Container>
        );
    }

    if (successBillId) {
        return (
            <Box sx={{ bgcolor: '#F7F3EE', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Container maxWidth="sm">
                    <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, textAlign: 'center',
                        border: '1px solid rgba(74,103,65,0.2)', bgcolor: '#fff' }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: '4rem', color: '#4A6741', mb: 2 }} />
                        <Typography variant="h5" fontWeight={800} sx={{ color: WALNUT, mb: 1,
                            fontFamily: "'Playfair Display', Georgia, serif" }}>
                            Booking Received!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Thank you for your booking. Our team will be in touch with you shortly via your provided contact details.
                        </Typography>
                        <Box sx={{ bgcolor: 'rgba(74,103,65,0.07)', borderRadius: 2, px: 3, py: 2, mb: 3 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Reference ID
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {successBillId}
                            </Typography>
                        </Box>
                        <Button variant="outlined" component="a" href="/"
                                sx={{ borderColor: TERRACOTTA, color: TERRACOTTA, borderRadius: 2, fontWeight: 700 }}>
                            Back to Home
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    // Build line items
    const lineItems = [];
    const surchargeRate = state.tourType === 'BIKE' ? 0.30 : 0;
    ['local', 'foreign'].forEach(paxType => {
        const group = state.booking?.[paxType];
        if (!group) return;
        Object.entries(group).forEach(([category, counts]) => {
            if (!counts || typeof counts !== 'object') return;
            const { normal = 0, electric = 0 } = counts;
            const unitPrice = state.prices?.[paxType]?.[category] || 0;
            const label = `${paxType === 'local' ? 'Local' : 'Tourist'} ${category.charAt(0) + category.slice(1).toLowerCase()}`;
            if (normal > 0) {
                lineItems.push({
                    label, qty: normal, unitPrice, amount: unitPrice * normal,
                    paxType, category,
                    description: `${paxType.toUpperCase()} ${category.toUpperCase()}`,
                });
            }
            if (electric > 0) {
                const surcharge = Math.round(unitPrice * surchargeRate);
                const eBikePrice = unitPrice + surcharge;
                lineItems.push({
                    label: `${label}${surchargeRate > 0 ? ' (E-Bike +30%)' : ''}`,
                    qty: electric, unitPrice: eBikePrice, amount: eBikePrice * electric,
                    paxType, category,
                    description: `${paxType.toUpperCase()} ${category.toUpperCase()} ELECTRIC`,
                });
            }
        });
    });
    const subtotal = lineItems.reduce((s, i) => s + i.amount, 0);
    const tax = state.total - subtotal;

    const handleConfirm = async () => {
        setLoading(true); setIsSubmitting(true); setBookingError('');
        try {
            if (user && !user.phone && contact.phone) {
                const updateData = new FormData();
                updateData.append("user", new Blob([JSON.stringify({
                    username: user.username || '', phone: contact.phone,
                    idNumber: user.idNumber || '', role: user.role || '', removeImage: false,
                })], { type: "application/json" }));
                const res = await axios.put(`/api/app_user/edit/${user.id}`, updateData);
                setUser(prev => ({ ...prev, phone: res.data.phone }));
            }

            const convertedTotal = convertCents(state.total, tourCurrency, currency, rates);
            const rate           = getExchangeRate(tourCurrency, currency, rates);

            const billPayload = {
                subtotal:     convertedTotal,
                totalAmount:  convertedTotal,
                billStatus:   isManager ? "PAID" : "DRAFT",
                offer:        { id: tourId },
                customer:     user ? { id: user.id } : null,
                guestPhone:   user ? null : (contact.phone || null),
                guestEmail:   user ? null : (contact.email || null),
                tourDate:     state.tourDate,
                startTime:    state.startTime,
                endTime:      state.endTime,
                currency:     currency,
                baseCurrency: tourCurrency,
                baseAmount:   state.total,
                exchangeRate: rate,
                notes:        contact.notes || null,
            };

            const http = user ? axios : axiosPublic;
            const billResponse = await http.post("/bills/bill", billPayload);
            const billId = billResponse.data.id;

            await Promise.all(lineItems.map(item =>
                http.post("/bill_items/bill_item", {
                    bill: { id: billId }, quantity: item.qty,
                    description: item.description || `${item.paxType.toUpperCase()} ${item.category.toUpperCase()}`,
                    unitPrice: item.unitPrice, amount: item.amount,
                    refundable: true, billing: "BASE",
                })
            ));

            if (user) {
                navigate(`/booking/${billId}`, { replace: true });
            } else {
                setSuccessBillId(billId);
                setLoading(false);
                setIsSubmitting(false);
            }
        } catch (err) {
            setIsSubmitting(false); setLoading(false);
            setBookingError(
                err?.response?.data?.message ||
                (err?.response ? 'Something went wrong. Please try again.' : err?.message) ||
                'Booking failed. Please try again.'
            );
        }
    };

    const tourDateLabel = state.tourDate
        ? new Date(state.tourDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
        : null;

    const heroImg = state.tourType === 'BIKE'
        ? 'https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg'
        : 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Yerevan_Armenia_with_the_backdrop_of_Mount_Ararat.JPG';

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>

            {/* ── Left: Full-height image panel (desktop only) ──────────── */}
            <Box sx={{
                width: '50%',
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                position: 'sticky',
                top: 0,
                height: '100vh',
                flexShrink: 0,
                overflow: 'hidden',
            }}>
                <Box sx={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url("${heroImg}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }} />
                <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(44,31,20,0.88) 0%, rgba(44,31,20,0.35) 55%, rgba(44,31,20,0.08) 100%)',
                }} />
                <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', p: { md: 4, lg: 5 } }}>
                    <Typography variant="overline"
                                sx={{ color: 'rgba(255,255,255,0.55)', letterSpacing: 3, fontSize: '0.7rem' }}>
                        {isManager ? 'Manual Entry' : t('checkout.finalize', 'Complete Booking')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="#fff" sx={{
                        mt: 0.5, mb: 2,
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: { md: '1.75rem', lg: '2.125rem' },
                    }}>
                        {state.title || t('checkout.finalize')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {tourDateLabel && (
                            <Chip
                                icon={<EventIcon sx={{ fontSize: '0.9rem !important', color: TERRACOTTA }} />}
                                label={tourDateLabel}
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600,
                                      border: '1px solid rgba(255,255,255,0.18)' }}
                            />
                        )}
                        {state.startTime && (
                            <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: '0.9rem !important', color: TERRACOTTA }} />}
                                label={`${state.startTime}${state.endTime ? ` – ${state.endTime}` : ''}`}
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600,
                                      border: '1px solid rgba(255,255,255,0.18)' }}
                            />
                        )}
                    </Stack>
                </Box>
            </Box>

            {/* ── Right: Scrollable content panel ───────────────────────── */}
            <Box sx={{
                width: { xs: '100%', md: '50%' },
                minHeight: '100vh',
                overflowY: 'auto',
                background: `linear-gradient(rgba(247,243,238,0.92), rgba(247,243,238,0.92)), url("https://upload.wikimedia.org/wikipedia/commons/d/d5/Yerevan_Armenia_with_the_backdrop_of_Mount_Ararat.JPG") center/cover fixed`,
                py: { xs: 4, md: 5 },
                px: { xs: 2, sm: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}>

                {/* Mobile title (visible when image panel is hidden) */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Typography variant="overline"
                                sx={{ color: 'text.secondary', letterSpacing: 3, fontSize: '0.7rem' }}>
                        {isManager ? 'Manual Entry' : t('checkout.finalize', 'Complete Booking')}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}
                                sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                        {state.title || t('checkout.finalize')}
                    </Typography>
                    {(tourDateLabel || state.startTime) && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mt={1}>
                            {tourDateLabel && (
                                <Chip icon={<EventIcon sx={{ fontSize: '0.9rem !important', color: TERRACOTTA }} />}
                                      label={tourDateLabel} size="small"
                                      sx={{ bgcolor: 'rgba(196,98,45,0.08)', color: WALNUT, fontWeight: 600 }} />
                            )}
                            {state.startTime && (
                                <Chip icon={<AccessTimeIcon sx={{ fontSize: '0.9rem !important', color: TERRACOTTA }} />}
                                      label={`${state.startTime}${state.endTime ? ` – ${state.endTime}` : ''}`} size="small"
                                      sx={{ bgcolor: 'rgba(196,98,45,0.08)', color: WALNUT, fontWeight: 600 }} />
                            )}
                        </Stack>
                    )}
                </Box>

                    {/* ── Contact form ──────────────────────────────────────── */}
                    <Box>
                        <Paper elevation={0} sx={{
                            p: { xs: 3, md: 4 },
                            borderRadius: 3,
                            border: '1px solid rgba(0,0,0,0.07)',
                            bgcolor: '#fff',
                        }}>
                            {/* Section header */}
                            <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '50%',
                                    bgcolor: 'rgba(196,98,45,0.10)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <PersonIcon sx={{ color: TERRACOTTA, fontSize: '1.15rem' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700} lineHeight={1.1}>
                                        {t('checkout.your_details', 'Your Details')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('checkout.details_sub', 'We\'ll use this to confirm your booking')}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack spacing={2.5}>
                                <TextField
                                    label={t('checkout.full_name')}
                                    fullWidth
                                    value={contact.fullName}
                                    onChange={e => setContact({ ...contact, fullName: e.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Email"
                                    type="email"
                                    required={!user}
                                    fullWidth
                                    value={contact.email}
                                    onChange={e => setContact({ ...contact, email: e.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label={t('checkout.phone')}
                                    required
                                    fullWidth
                                    value={contact.phone}
                                    onChange={e => setContact({ ...contact, phone: e.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label={t('checkout.notes')}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={contact.notes}
                                    onChange={e => setContact({ ...contact, notes: e.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                                <NotesIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>
                        </Paper>
                    </Box>

                    {/* ── Order summary ─────────────────────────────────────── */}
                    <Box>
                        <Box sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(196,98,45,0.18)',
                            overflow: 'hidden',
                            bgcolor: '#fff',
                            boxShadow: '0 4px 24px rgba(196,98,45,0.08)',
                        }}>
                            {/* Receipt header */}
                            <Box sx={{
                                px: 3, py: 2,
                                bgcolor: 'rgba(196,98,45,0.06)',
                                borderBottom: '1px dashed rgba(196,98,45,0.25)',
                            }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <ConfirmationNumberIcon sx={{ color: TERRACOTTA, fontSize: '1.1rem' }} />
                                    <Typography variant="subtitle1" fontWeight={700} color={WALNUT}>
                                        {t('checkout.summary')}
                                    </Typography>
                                </Stack>
                            </Box>

                            <Box sx={{ p: 3 }}>
                                {/* Line items */}
                                <Stack spacing={1.4} mb={2}>
                                    {lineItems.length > 0 ? lineItems.map((item, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', width: '100%' }}>
                                            <Typography variant="body2" sx={{ flexShrink: 0, color: WALNUT }}>
                                                {item.qty}×&nbsp;{item.label}
                                                <Typography component="span" variant="caption"
                                                            sx={{ color: 'text.secondary', ml: 0.5 }}>
                                                    ({fmt(item.unitPrice)})
                                                </Typography>
                                            </Typography>
                                            <Box sx={{ flexGrow: 1, mx: 1.5,
                                                        borderBottom: '2px dotted rgba(0,0,0,0.14)',
                                                        position: 'relative', top: '-4px', minWidth: 12 }} />
                                            <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, color: WALNUT }}>
                                                {fmt(item.amount)}
                                            </Typography>
                                        </Box>
                                    )) : (
                                        <Typography variant="body2" color="text.secondary">No items selected.</Typography>
                                    )}
                                </Stack>

                                <Divider sx={{ borderStyle: 'dashed', borderColor: 'rgba(0,0,0,0.1)', mb: 1.5 }} />

                                {/* Subtotal */}
                                <Stack direction="row" justifyContent="space-between" mb={0.8}>
                                    <Typography variant="body2" color="text.secondary">{t('booking.subtotal')}</Typography>
                                    <Typography variant="body2" fontWeight={600}>{fmt(subtotal)}</Typography>
                                </Stack>

                                {tax !== 0 && (
                                    <Stack direction="row" justifyContent="space-between" mb={0.8}>
                                        <Typography variant="body2" color="text.secondary">{t('booking.tax')}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{fmt(tax)}</Typography>
                                    </Stack>
                                )}

                                <Divider sx={{ my: 2 }} />

                                {/* Total */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h6" fontWeight={700} color={WALNUT}>
                                        {t('checkout.total')}
                                    </Typography>
                                    <Typography variant="h5" fontWeight={900} sx={{
                                        color: TERRACOTTA,
                                        fontFamily: "'Playfair Display', Georgia, serif",
                                    }}>
                                        {fmt(state.total)}
                                    </Typography>
                                </Stack>

                                {/* Booking error */}
                                {bookingError && (
                                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                                        {bookingError}
                                    </Alert>
                                )}

                                {/* Loading state */}
                                {loading ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                                               gap: 1.5, py: 2 }}>
                                        <CircularProgress size={32} sx={{ color: TERRACOTTA }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Saving your booking…
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        startIcon={<LockIcon />}
                                        onClick={handleConfirm}
                                        disabled={!contact.phone || (!user && !contact.email)}
                                        sx={{
                                            py: 1.6,
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            borderRadius: 2,
                                            background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #a84f24 100%)`,
                                            boxShadow: '0 4px 14px rgba(196,98,45,0.35)',
                                            '&:hover': {
                                                background: `linear-gradient(135deg, #b5561e 0%, #8f3f19 100%)`,
                                                boxShadow: '0 6px 18px rgba(196,98,45,0.45)',
                                            },
                                            '&.Mui-disabled': { opacity: 0.45 },
                                        }}
                                    >
                                        {t('checkout.confirm')}
                                    </Button>
                                )}

                                {/* Phone required hint */}
                                {!contact.phone && !loading && (
                                    <Typography variant="caption" color="text.secondary"
                                                sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                        {t('checkout.phone_required', 'A phone number is required to confirm')}
                                    </Typography>
                                )}
                            </Box>

                            {/* Receipt footer tear line */}
                            <Box sx={{
                                height: 10,
                                background: 'repeating-linear-gradient(90deg, #F7F3EE 0px, #F7F3EE 8px, transparent 8px, transparent 14px)',
                                borderTop: '1px dashed rgba(196,98,45,0.18)',
                            }} />
                        </Box>
                    </Box>
            </Box>
        </Box>
    );
}
